// import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
// import type { LoginUser, Role } from "@/mocks/authData";
// import { mockLoginUsers } from "@/mocks/authData";

// interface AuthState {
//   user: (LoginUser & { role: Role }) | null;
//   isLoading: boolean;
//   isAuthenticated: boolean;
// }

// interface AuthContextValue extends AuthState {
//   login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
//   logout: () => void;
//   sendResetLink: (email: string) => Promise<boolean>;
//   resetPassword: (newPassword: string) => Promise<boolean>;
// }

// const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// const STORAGE_KEY = "crm_user";

// function simulateDelay(ms = 800): Promise<void> {
//   return new Promise((resolve) => setTimeout(resolve, ms));
// }

// export function AuthProvider({ children }: { children: ReactNode }) {
//   const [user, setUser] = useState<LoginUser | null>(null);
//   const [isLoading, setIsLoading] = useState(true);

//   useEffect(() => {
//     const stored = localStorage.getItem(STORAGE_KEY);
//     if (stored) {
//       try {
//         const parsed = JSON.parse(stored) as LoginUser;
//         setUser(parsed);
//       } catch {
//         localStorage.removeItem(STORAGE_KEY);
//       }
//     }
//     setIsLoading(false);
//   }, []);

//   const login = useCallback(async (email: string, password: string) => {
//     await simulateDelay(1200);

//     const found = mockLoginUsers.find(
//       (u) => u.email === email && u.password === password
//     );

//     if (!found) {
//       return { success: false, error: "Invalid email or password" };
//     }
// console.log()
//     localStorage.setItem(STORAGE_KEY, JSON.stringify(found));
//     setUser(found);
//     return { success: true };
//   }, []);

//   const logout = useCallback(() => {
//     localStorage.removeItem(STORAGE_KEY);
//     setUser(null);
//   }, []);

//   const sendResetLink = useCallback(async (email: string) => {
//     await simulateDelay(1000);
//     const exists = mockLoginUsers.some((u) => u.email === email);
//     return exists;
//   }, []);

//   const resetPassword = useCallback(async (_newPassword: string) => {
//     await simulateDelay(1000);
//     return true;
//   }, []);

//   const value: AuthContextValue = {
//     user,
//     isLoading,
//     isAuthenticated: user !== null,
//     login,
//     logout,
//     sendResetLink,
//     resetPassword,
//   };

//   return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
// }

// export function useAuth(): AuthContextValue {
//   const ctx = useContext(AuthContext);
//   if (!ctx) {
//     throw new Error("useAuth must be used within an AuthProvider");
//   }
//   return ctx;
// }


import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import axios from "axios";

// ── Types ──────────────────────────────────────────────────────────────────
type Role = string;

interface AuthUser {
  id: number;
  name: string;
  email: string;
  role: Role;
  permissions: Record<string, any>;
  additionalControls: Record<string, any>;
  companyId: number | null;
}

interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  sendResetLink: (email: string) => Promise<{ success: boolean; error?: string }>;
  resetPassword: (token: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const API_BASE = import.meta.env.VITE_API_URL || "https://asvapi.digiindiasolutions.com/api/v1";

// Axios instance — withCredentials is essential so httpOnly cookies (token, refreshToken) are sent/received
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // On mount: verify session with backend using the httpOnly cookie (not localStorage)
  useEffect(() => {
    const checkSession = async () => {
      try {
        const res = await api.get("/auth/me");
        console.log("res===>", res.data.data)
        setUser(res.data?.data ?? null);
      } catch {
        // No valid session — user stays null
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const res = await api.post("/auth/login", { email, password });
      const loggedInUser = res.data?.data?.user;

      setUser(loggedInUser);
      
      localStorage.setItem('auth_token', res.data?.data?.accessToken);
      return { success: true, loggedInUser: loggedInUser };
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        "Invalid email or password";
      return { success: false, error: message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      console.error("Logout request failed:", error);
      // proceed with client-side logout regardless
    } finally {
      setUser(null);
    }
  }, []);

  const sendResetLink = useCallback(async (email: string) => {
    try {
      await api.post("/auth/forgot-password", { email });
      return { success: true };
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        "Failed to send reset link";
      return { success: false, error: message };
    }
  }, []);

  const resetPassword = useCallback(async (token: string, newPassword: string) => {
    try {
      await api.post(`/auth/reset-password/${token}`, { newPassword });
      return { success: true };
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        "Failed to reset password";
      return { success: false, error: message };
    }
  }, []);

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated: user !== null,
    login,
    logout,
    sendResetLink,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}